@let flatGroupComponents = flatGroupComponent$ | async;
@let noPatternMatches =
  !!(patternMatchDataService.pmAnalysis.lastSuccessfulData$ | async)
    ?.patternMatches?.length;

<ng-template #overviewTabs>
  <sh-tabs role="tablist" *ngIf="isIntegratedContentAvailable">
    <sh-tab-item
      label="{{ 'label.ComponentOverview' | translate }}"
      [active]="selectedTab === 'overview'"
      (click)="activateTab('overview')"
      (keydown.enter)="activateTab('overview')"
      item
      id="tab-overview"
    ></sh-tab-item>
    <sh-tab-item
      label="Content Integration"
      [active]="selectedTab === 'content'"
      (click)="activateTab('content')"
      (keydown.enter)="activateTab('content')"
      item
      id="tab-content"
    ></sh-tab-item>
  </sh-tabs>
</ng-template>

@if (selectedTab === 'overview') {
  <sh-card
    columns="12"
    [label]="
      (hasNonUnnamedComponents$ | async)
        ? ('label.ComponentStatus' | translate)
        : ('label.patternMatches' | translate)
    "
  >
    <ng-container *ngTemplateOutlet="overviewTabs"></ng-container>

    <sh-icon
      slot="functions"
      button
      aria-label="Send System Report by Email"
      (click)="sendSystemReportByEmail()"
      size="s"
      icon="envelope"
      id="envelope-icon"
    ></sh-icon>
    <!-- to trace -->
    <sh-icon
      slot="functions"
      button
      (click)="copySystemReportToClipboard()"
      size="s"
      aria-label="Copy results to clipboard"
      icon="copy-results-to-clipboard"
      id="clipboard-icon"
    ></sh-icon>
    <app-refresh-data-sources-button
      [apiCall]="refreshableApiCalls"
      (didChange)="indicateRefresh($event)"
      slot="functions"
      id="refresh-icon"
    ></app-refresh-data-sources-button>

    @if (showSystemReportPopover) {
      <sh-popover
        class="popover"
        [label]="popoverLabel | translate"
        [type]="popoverType"
        [target]="popoverTarget"
        sticky
        position="bottom-right"
        [style.borderLeft]="'4px solid ' + getPopoverBorderColor()"
      >
        @if (popoverState === 'progress') {
          <span
            ><sh-progress
              [header]="progressHeader | translate"
              indeterminate
              radial
              [info]="'message.systemInfo' | translate"
              size="s"
            ></sh-progress>
          </span>
        }

        @if (popoverState === 'success') {
          <p>
            {{ popoverMessage | translate }}
          </p>
        }

        @if (popoverState === 'failure') {
          {{ popoverMessage | translate }}
        }

        <sh-icon
          slot="functions"
          button
          (click)="closeSystemReportPopover()"
          size="s"
          icon="cancel"
        ></sh-icon>
      </sh-popover>
    }

    <api-call-gate [apiCall]="systemOverviewService.entireOverview">
      <ng-template #lastSuccessfulData let-entireOverview>
        @if ((hasNonUnnamedComponents$ | async) === false) {
          <app-pattern-match
            [systemDetails]="systemDetails$ | async"
            [filterName]="patternMatchDataService.filterName$ | async"
          ></app-pattern-match>

          @if (noPatternMatches) {
            <sh-button
              class="show-details-button"
              color="secondary"
              label="Show Details"
              size="m"
              (click)="
                goToComponent(flatGroupComponents![0]); $event.preventDefault()
              "
            ></sh-button>
          }
        }

        @if (hasNonUnnamedComponents$ | async) {
          <div class="groups">
            @for (group of processedComponentGroups$ | async; track $index) {
              @if (group.components.length > 0) {
                <div
                  [class]="
                    group.className +
                    ' group' +
                    (group.topGroup || isComponentExpanded ? ' expanded' : '')
                  "
                >
                  <div class="components">
                    @for (component of group.components; track $index) {
                      <a
                        class="item"
                        id="componentId_{{ component.id }}"
                        [attr.icon]="component.stateIcon"
                        [attr.flash]="
                          (component.id !== undefined &&
                            changedComponents.has(component.id)) ||
                          null
                        "
                        (mouseenter)="mouseEnterComponent(component)"
                        (mouseleave)="mouseLeaveComponent()"
                        [attr.disabled]="component.isActive === false"
                        [href]="component.webAppEndpoint"
                        (click)="
                          goToComponent(component); $event.preventDefault()
                        "
                      >
                        <sh-icon
                          size="s"
                          [icon]="
                            !component.stateIcon ||
                            component.stateIcon === 'circle-filled'
                              ? 'no-findings'
                              : component.stateIcon
                          "
                        ></sh-icon>
                        <div class="layout">
                          <span>{{ component.displayName }}</span>

                          @if (component.info) {
                            <span class="info">{{ component.info }}</span>
                          }
                        </div>
                      </a>
                    }
                  </div>
                </div>
              }
            }

            <sh-divider spacing="m"></sh-divider>

            <!-- coil AND tube -->
            <div
              class="data-status"
              class="container-pattern-match"
              columns="8"
              col-m="8"
              col-s="12"
            >
              <app-lastDataSourceRefresh
                [systemDetails]="systemDetails$ | async"
                [modalityName]="modalityName$ | async"
              >
              </app-lastDataSourceRefresh>
            </div>
          </div>
        }
      </ng-template>
    </api-call-gate>
  </sh-card>
}

@if (selectedTab === 'content') {
  <sh-card columns="12">
    <ng-container *ngTemplateOutlet="overviewTabs"></ng-container>

    <div class="iframe-wrapper" #iframeWrapper>
      <div class="fullscreen">
        @if (!isFullscreenActive) {
          <sh-icon
            [icon]="'fullscreen'"
            size="s"
            button="true"
            (click)="toggleFullscreen()"
            class="background-gray"
          ></sh-icon>
        }
        @if (isFullscreenActive) {
          <sh-icon
            [icon]="'exit-fullscreen'"
            size="s"
            button="true"
            (click)="toggleFullscreen()"
            class="background-gray"
          ></sh-icon>
        }
      </div>

      <div class="zoom-controls">
        <sh-icon
          [icon]="'zoom-minus'"
          size="s"
          button="true"
          (click)="zoomOut()"
          class="background-gray"
        ></sh-icon>
        <sh-icon
          [icon]="'zoom-plus'"
          size="s"
          button="true"
          (click)="zoomIn()"
          class="background-gray"
        ></sh-icon>
      </div>

      <div class="iframe-scroll">
        <iframe
          id="myFrame"
          [src]="safeAdditionalDataUrl"
          [style.width.%]="100 * zoomLevel"
          [style.height.%]="100 * zoomLevel"
          style="border: 0"
          frameborder="0"
        ></iframe>
      </div>
    </div>
  </sh-card>
}

@if (!!tooltipText) {
  <sh-tooltip
    #tooltip
    style="transform: translate(4rem, 0); pointer-events: none"
    placement="top-left"
    variation="short"
    delay
    [attr.visible]="tooltipVisible"
    [target]="tooltipTarget"
  >
    <sh-text>{{ tooltipText }}</sh-text>
  </sh-tooltip>
}

@if ((screenLayoutService.phone$ | async) === false) {
  <sh-tooltip
    [label]="'label.copyToClipboard.tooltip' | translate"
    target="clipboard-icon"
  ></sh-tooltip>
  <sh-tooltip
    [label]="'label.openSystemInfoEmailTo.tooltip' | translate"
    target="envelope-icon"
  ></sh-tooltip>
}
